import { MathUtils, Mesh, Object3D, Material } from 'three'

interface Difficulty {
  minCombo: number
  [key: string]: number
}

interface Params {
  [key: string]: any
  difficulty: {
    lowDifficulty: Difficulty
    highDifficulty: Difficulty
  }
}

/**
 * Returns a lerped value based on specified combo.
 * If combo is lower than lowDifficulty.minCombo, we always use lowDifficulty[property] without interpolating.
 * If combo is higher than highDifficulty.minCombo, we alway use highDifficulty[property] without interpolating.
 * If combo is somewhere in betwen, we map combo value from range [lowDifficulty.minCombo, highDifficulty.minCombo] to [lowDifficulty[property], highDifficulty[property]].
 * Example:
 * ````
 * const PARAMS = {
 *  difficulty: {
 *    lowDifficulty: { minCombo: 10, count: 0 },
 *    highDifficulty: { minCombo: 20, count: 1000 },
 *  }
 * }
 *
 * lerpDifficultyProperty(PARAMS, 'count', 2) // 0
 * lerpDifficultyProperty(PARAMS, 'count', 15) // 500
 * lerpDifficultyProperty(PARAMS, 'count', 40) // 1000
 * ````
 */
function lerpDifficultyProperty(
  params: Params,
  property: string,
  combo: number
) {
  if (combo < params.difficulty.lowDifficulty.minCombo) {
    return params.difficulty.lowDifficulty[property]
  }

  if (combo > params.difficulty.highDifficulty.minCombo) {
    return params.difficulty.highDifficulty[property]
  }

  return MathUtils.mapLinear(
    MathUtils.clamp(
      combo,
      params.difficulty.lowDifficulty.minCombo,
      params.difficulty.highDifficulty.minCombo
    ),
    params.difficulty.lowDifficulty.minCombo,
    params.difficulty.highDifficulty.minCombo,
    params.difficulty.lowDifficulty[property],
    params.difficulty.highDifficulty[property]
  )
}

/**
 * Traverse whole hierarchy, going through all materials.
 * For each material, create its equivalent using a ToonMeshMaterial that is stored in a map and reused thoughout the hierarchy.
 */
function updateMaterials(
  root: Object3D,
  updateMaterialCallback: (m: Mesh<any, any>['material']) => Material
) {
  const materials = new Map<string, Material>()

  root.traverse(object => {
    if (object instanceof Mesh && object.material) {
      // We have this material in the map, reuse it
      if (materials.has(object.material.name)) {
        const material = materials.get(object.material.name)
        object.material = material
      } else {
        // We don't know about this material yet, create an equivalent ToonMeshShader using the same color and store it in the map
        const material = updateMaterialCallback(object.material)

        object.material = material

        materials.set(object.material.name, material)
      }
    }
  })
}

export { lerpDifficultyProperty, updateMaterials }
export type { Params }
